import {
  asString,
  normalizeColumnName,
  samePoint,
  splitNonEmptyLines,
  toNumber,
} from "./formatHandlers/formatUtils.js";

// find the best column header for the given candidates names and regexes rules
// gives a columns list
// compare the headers to the candidates names and return if match
// if no match compare to a regex list and return if match
// else return null
function pickColumnByNormalized(columns, { candidates = [], regexes = [] } = {}) {
  if (!Array.isArray(columns) || columns.length === 0) return null;

  const byNorm = new Map(columns.map((c) => [normalizeColumnName(c), c]));

  for (const cand of candidates) {
    const hit = byNorm.get(normalizeColumnName(cand));
    if (hit) return hit;
  }

  if (regexes.length > 0) {
    for (const col of columns) {
      const n = normalizeColumnName(col);
      if (regexes.some((re) => re.test(n))) return col;
    }
  }

  return null;
}

function detectDetailedHeaderRowIdx(lines) {
  // We detect the "detailed header" row by looking for a row that contains:
  // - a point index column
  // - X and Y columns
  for (let i = 0; i < Math.min(lines.length, 10); i++) {
    const lNorm = normalizeColumnName(lines[i]);
    if (lNorm.includes("n_point") && lNorm.includes("x") && lNorm.includes("y")) return i;
  }
  return -1;
}

function buildHeadersFromTwoRows(categoryRow, detailedRow) {
  // This parser historically expects semicolon-separated CSV, but files can be
  // re-saved by Excel/LibreOffice with commas depending on locale.
  const sep = detectCsvSeparator(detailedRow || categoryRow || "");
  // Reconstruct a single header line:
  // - if the detailed cell is non-empty, use it
  // - otherwise fallback to the category cell at the same index
  const cat = (categoryRow || "").split(sep);
  const det = (detailedRow || "").split(sep);
  return det.map((_, idx) => {
    const a = (det[idx] ?? "").trim();
    const b = (cat[idx] ?? "").trim();
    // Some exports prepend an UTF-8 BOM (\uFEFF) at the very start of the first header cell.
    // Keep it out of column matching.
    const chosen = (a !== "" ? a : b).replace(/^\uFEFF/, "");
    return chosen;
  });
}

function detectCsvSeparator(sampleLine) {
  const s = String(sampleLine || "");
  const semi = (s.match(/;/g) || []).length;
  const comma = (s.match(/,/g) || []).length;
  const tab = (s.match(/\t/g) || []).length;
  if (tab > semi && tab > comma) return "\t";
  if (comma > semi) return ",";
  return ";";
}

function parseRows(headers, dataLines, separator) {
  return dataLines
    .map((l) => l.split(separator))
    .filter((cells) => cells.some((c) => String(c || "").trim() !== ""))
    .map((cells) => {
      const row = {};
      for (let i = 0; i < headers.length; i++) {
        const key = headers[i] ?? `col_${i}`;
        row[key] = cells[i] ?? "";
      }
      return row;
    });
}

function parseGeometryKind(rawValue) {
  const n = normalizeColumnName(rawValue);
  if (!n) return null;
  if (n === "ponctuel" || n === "point") return "Point";
  if (n === "lineaire" || n === "line" || n === "linestring") return "LineString";
  if (n === "surfacique" || n === "surface" || n === "polygon" || n === "polygone") return "Polygon";
  return null;
}

function validateGeometryForKind({ id, kind, coords2d }) {
  if (kind === "Point") {
    if (coords2d.length !== 1) {
      return `Invalid point geometry for id '${id}': expected exactly 1 point, got ${coords2d.length}.`;
    }
    return null;
  }

  if (kind === "LineString") {
    if (coords2d.length < 2) {
      return `Invalid line geometry for id '${id}': expected at least 2 points, got ${coords2d.length}.`;
    }
    return null;
  }

  if (kind === "Polygon") {
    // We do NOT require a closed ring on import. If the polygon is closed (last point == first),
    // we will drop the last duplicate point later so we don't keep implicit closure in stored geometry.
    if (coords2d.length < 3) {
      return `Invalid polygon geometry for id '${id}': expected at least 3 points, got ${coords2d.length}.`;
    }
    return null;
  }

  return `Invalid geometry for id '${id}'. Expected: PONCTUEL | LINEAIRE | SURFACIQUE`;
}

/**
 * Parse a "point-per-line shape" CSV (1 vertex per line, vertex order column) into GeoJSON.
 *
 * This format is common in inspection exports where a single "annotation"/"defect" geometry
 * is described by multiple rows (one row per vertex), with an explicit point index to rebuild
 * the geometry in order.
 *
 * ## Mandatory columns
 * - **Feature id**: `Matricule` (fallbacks: `id`, `ID`, `Id`)
 * - **Geometry kind**: `geometry` with values `PONCTUEL` | `LINEAIRE` | `SURFACIQUE`
 * - **Vertex order**: `N° Point` (fallbacks: `NumPoint`, `No Point`, etc.)
 * - **Coordinates**: `X (m)` and `Y (m)` (fallbacks: `X`, `Y`)
 *
 * ## Geometry reconstruction rules
 * - Group all rows by the feature id column.
 * - Sort each group by the vertex-order column (numeric).
 * - Geometry built from the point count:
 *   - 1 point → `Point`
 *   - 2+ points → `LineString`
 *
 * ## Returns
 * - `null` if the file doesn't look like this format (so callers can fallback to another parser)
 * - otherwise `{ geojson, columns, errors }`
 */
export function parsePointPerLineShapeCsv(csvText) {
  const data = parsePointPerLineShapeCsvData(csvText);
  if (!data) return null;
  return pointPerLineShapeDataToGeojson(data);
}

export function parsePointPerLineShapeCsvData(csvText) {
  // Step 1 — Read lines and locate the header row that contains point/X/Y columns.
  const lines = splitNonEmptyLines(csvText);

  // Allow very small exports (header + 1 row).
  if (lines.length < 2) return null;

  const header2Idx = detectDetailedHeaderRowIdx(lines);
  if (header2Idx < 0) return null;

  // Step 2 — Build final headers (handles "multi-row headers" exports).
  const headerLine = lines[header2Idx] || "";
  const separator = detectCsvSeparator(headerLine);
  const headers = buildHeadersFromTwoRows(lines[header2Idx - 1], headerLine);

  // Step 3 — Parse all data lines into row objects.
  const dataLines = lines.slice(header2Idx + 1);
  if (dataLines.length === 0) return null;

  const rows = parseRows(headers, dataLines, separator);
  const columns = headers.filter((h) => String(h || "").trim() !== "");

  // Step 4 — Identify required columns (robust to casing/accents/special chars).
  const idKey = pickColumnByNormalized(columns, { candidates: ["Matricule", "ID", "id"] });
  const geometryKey =
    pickColumnByNormalized(columns, { candidates: ["geometry", "geometry_type", "type", "Géométrie"] }) ||
    pickColumnByNormalized(columns, { regexes: [/^geometry(_type)?$/, /^geometrie$/, /^type$/] });
  const orderKey =
    pickColumnByNormalized(columns, {
      candidates: ["N° Point", "Numéro de point", "NumPoint", "Point number", "Vertex index"],
    }) ||
    pickColumnByNormalized(columns, {
      regexes: [
        /^n?_?point(_number|_index)?$/,
        /^numero(_de)?_point$/,
        /^num(_)?point$/,
        /^point(_)?number$/,
        /^vertex(_number|_index)?$/,
      ],
    });
  const xKey =
    pickColumnByNormalized(columns, { candidates: ["X (m)", "X", "x"] }) ||
    pickColumnByNormalized(columns, { regexes: [/^x($|_)/] });
  const yKey =
    pickColumnByNormalized(columns, { candidates: ["Y (m)", "Y", "y"] }) ||
    pickColumnByNormalized(columns, { regexes: [/^y($|_)/] });

  const missing = [
    !idKey && "id (or Matricule)",
    !geometryKey && "geometry",
    !orderKey && "N° Point / NumPoint",
    !xKey && "X",
    !yKey && "Y",
  ].filter(Boolean);

  const errors = [];
  if (missing.length > 0) {
    errors.push(`Unsupported CSV format: missing column(s): ${missing.join(", ")}.`);
    errors.push(`Detected columns: ${columns.join(" | ")}`);
  }

  return {
    columns,
    rows,
    errors,
    keys: missing.length > 0 ? null : { idKey, geometryKey, orderKey, xKey, yKey },
  };
}

export function pointPerLineShapeDataToGeojson(data) {
  const columns = data?.columns || [];
  const errors = [...(data?.errors || [])];

  if (!data?.keys) {
    return { geojson: { type: "FeatureCollection", features: [] }, columns, errors };
  }

  const { idKey, geometryKey, orderKey, xKey, yKey } = data.keys;
  const rows = data.rows || [];

  // Step 5 — Group CSV rows by defect/annotation id (one geometry per id).
  const byId = new Map();
  for (const row of rows) {
    const id = asString(row[idKey]).trim();
    if (!id) continue;
    const x = toNumber(row[xKey]);
    const y = toNumber(row[yKey]);
    if (x == null || y == null) continue;
    const order = toNumber(row[orderKey]);
    const geomRaw = row[geometryKey];
    const geometryKind = parseGeometryKind(geomRaw);
    if (!geometryKind) {
      errors.push(
        `Invalid geometry '${asString(geomRaw).trim()}' for id '${id}'. Expected: PONCTUEL | LINEAIRE | SURFACIQUE`,
      );
      continue;
    }

    if (!byId.has(id)) byId.set(id, []);
    byId.get(id).push({ row, x, y, order, geometryKind });
  }

  // Step 6 — For each id: sort points, validate, then build the GeoJSON feature.
  const features = [];
  for (const [id, pts] of byId.entries()) {
    const sorted = [...pts].sort((a, b) => {
      if (a.order == null && b.order == null) return 0;
      if (a.order == null) return 1;
      if (b.order == null) return -1;
      return a.order - b.order;
    });

    const firstRow = sorted[0]?.row || {};
    const allTypes = new Set(sorted.map((p) => p.geometryKind));
    if (allTypes.size > 1) {
      errors.push(
        `Inconsistent geometry values for id '${id}'. Expected a single value (PONCTUEL/LINEAIRE/SURFACIQUE) across all points.`,
      );
      continue;
    }
    const expectedType = sorted[0]?.geometryKind || null;

    let coords2d = sorted.map((p) => [p.x, p.y]);
    // If input polygon ring is already closed (first == last), drop the last point
    // to match our internal convention (we don't store the implicit closing vertex).
    if (expectedType === "Polygon" && coords2d.length >= 2 && samePoint(coords2d[0], coords2d[coords2d.length - 1])) {
      coords2d = coords2d.slice(0, -1);
    }

    // Step 6.a — Validate that the point count matches the declared geometry kind.
    const properties = { ...firstRow };
    delete properties[orderKey];
    delete properties[xKey];
    delete properties[yKey];
    delete properties[geometryKey];

    for (const [k, v] of Object.entries(properties)) {
      const s = asString(v).trim();
      if (s === "/" || s === "") delete properties[k];
    }
    properties[idKey] = id;

    const geometryError = validateGeometryForKind({ id, kind: expectedType, coords2d });
    if (geometryError) {
      errors.push(geometryError);
      continue;
    }

    // Step 6.b — Build the final GeoJSON geometry (only Point / LineString / Polygon).
    const geometryType = expectedType;
    const coordinates =
      geometryType === "Point" ? coords2d[0] : geometryType === "Polygon" ? [coords2d] : coords2d;

    features.push({
      type: "Feature",
      geometry: { type: geometryType, coordinates },
      properties,
    });
  }

  return { geojson: { type: "FeatureCollection", features }, columns, errors };
}

